<template>
  <button
    type="button"
    class="relative flex size-[44px] cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-[rgba(5,21,36,0.04)]"
    :aria-label="label"
    @click.stop="renderGoogleButton"
  >
    <GoogleIcon />
    <div
      id="login-google-third-party-target"
      ref="googleTargetRef"
      class="absolute inset-0 z-[10] flex items-center justify-center overflow-hidden opacity-[0.01]"
    ></div>
  </button>
</template>

<script lang="ts">
const GOOGLE_CLIENT_ID = '131257100694-p753d575b7j8upejdmfrmqsdle6n1988.apps.googleusercontent.com'
const GOOGLE_SCRIPT_SRC = 'https://accounts.google.com/gsi/client'

let googleScriptPromise: Promise<void> | null = null
let googleInitialized = false
let googleCredentialCallback: ((credential: string) => void) | null = null

/**
 * 加载 Google Identity SDK，多个弹窗实例复用同一个 script。
 */
const loadGoogleScript = () => {
  if (!import.meta.client) return Promise.resolve()

  if ((window as any).google?.accounts?.id) {
    return Promise.resolve()
  }

  if (googleScriptPromise) {
    return googleScriptPromise
  }

  googleScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>('script[data-wikibit-google-login]')
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true })
      existingScript.addEventListener('error', reject, { once: true })
      return
    }

    const script = document.createElement('script')
    script.src = GOOGLE_SCRIPT_SRC
    script.async = true
    script.dataset.wikibitGoogleLogin = 'true'
    script.addEventListener('load', () => resolve(), { once: true })
    script.addEventListener('error', reject, { once: true })
    document.head.appendChild(script)
  })

  return googleScriptPromise
}
</script>

<script setup lang="ts">
import GoogleIcon from '~/assets/icon/google.svg'

defineProps<{
  label: string
}>()

const emit = defineEmits<{
  credential: [credential: string]
}>()

const googleTargetRef = ref<HTMLElement | null>(null)
const rendered = ref(false)

/**
 * 组件挂载且 DOM 稳定后，只把 Google 官方按钮渲染到当前 target 一次。
 */
const renderGoogleButton = async () => {
  if (!import.meta.client || rendered.value) return

  await nextTick()
  await loadGoogleScript()

  const googleApi = (window as any).google?.accounts?.id
  const target = googleTargetRef.value
  if (!googleApi || !target || rendered.value) return

  googleCredentialCallback = (credential) => emit('credential', credential)

  if (!googleInitialized) {
    googleApi.initialize({
      client_id: GOOGLE_CLIENT_ID,
      ux_mode: 'popup',
      callback: (resp: Record<string, string>) => googleCredentialCallback?.(resp.credential || ''),
    })
    googleInitialized = true
  }

  target.innerHTML = ''
  googleApi.renderButton(target, {
    type: 'icon',
    size: 'large',
  })
  rendered.value = true
}

onMounted(() => {
  void renderGoogleButton()
})
</script>
